// cloud-admin i18next config — mirrors the operator app (apps/web/src/i18n).
// Loads per-namespace JSON from /locales/{lng}/{ns}.json (served from
// apps/cloud-admin/public/locales). English + Hebrew (RTL); language is cached
// in localStorage and the <html dir>/<html lang> are kept in sync.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

export const SUPPORTED_LANGUAGES = ['en', 'he'] as const;
export const LANGUAGE_LOCALSTORAGE_KEY = 'iosentra_admin_language';

const RTL_LANGUAGES: ReadonlySet<string> = new Set(['he', 'ar', 'fa', 'ur']);

export function isRtlLanguage(lng: string | undefined | null): boolean {
  if (!lng) return false;
  return RTL_LANGUAGES.has(lng.split('-')[0].toLowerCase());
}

/** Apply <html dir>/<html lang> for a language. Pure DOM; safe pre/post mount. */
export function applyDirAndLang(lng: string): void {
  if (typeof document === 'undefined') return;
  const base = lng.split('-')[0].toLowerCase();
  document.documentElement.lang = base;
  document.documentElement.dir = isRtlLanguage(base) ? 'rtl' : 'ltr';
}

const namespaces = ['admin', 'common'] as const;

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    ns: namespaces as unknown as string[],
    defaultNS: 'admin',
    interpolation: { escapeValue: false },
    backend: { loadPath: '/locales/{{lng}}/{{ns}}.json' },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_LOCALSTORAGE_KEY,
      caches: ['localStorage'],
    },
    react: { useSuspense: false },
  });

i18n.on('languageChanged', (lng) => applyDirAndLang(lng));

/** Switch language: persist + apply dir + tell i18next. */
export function setAdminLanguage(lng: string): void {
  localStorage.setItem(LANGUAGE_LOCALSTORAGE_KEY, lng);
  applyDirAndLang(lng);
  void i18n.changeLanguage(lng);
}

export default i18n;
